import { useState, useCallback, useEffect, useMemo } from 'react';
import { 
  type ChannelType, 
  type SubscriptionParams, 
  CHANNEL_PATTERNS,
  buildChannelName,
} from '@pineseed/spec';

export interface SubscriptionControlProps {
  onSubscribe: (channelType: ChannelType, channelName: string, params: SubscriptionParams) => void;
  onUnsubscribe: (channelName: string) => void;
  activeSubscriptions: Set<string>;
}

const STORAGE_KEY = 'admin-subscription-control';

export function SubscriptionControl({ onSubscribe, onUnsubscribe, activeSubscriptions }: SubscriptionControlProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  // Load from localStorage on mount
  const [params, setParams] = useState<SubscriptionParams>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { params: SubscriptionParams };
        return parsed.params || {};
      }
    } catch (error) {
      console.warn('Failed to load subscription params from localStorage:', error);
    }
    return {};
  });

  const [enabledChannels, setEnabledChannels] = useState<Set<ChannelType>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { enabledChannels: ChannelType[] };
        return new Set(parsed.enabledChannels || []);
      }
    } catch (error) {
      console.warn('Failed to load enabled channels from localStorage:', error);
    }
    return new Set();
  });

  // Persist to localStorage whenever params or enabledChannels change
  useEffect(() => {
    try {
      const state = {
        params,
        enabledChannels: Array.from(enabledChannels),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to persist subscription state to localStorage:', error);
    }
  }, [params, enabledChannels]);

  // Calculate which channels can be built based on current params
  const channelAvailability = useMemo(() => {
    const availability: Record<ChannelType, { canBuild: boolean; channelName: string | null }> = {} as Record<ChannelType, { canBuild: boolean; channelName: string | null }>;
    
    for (const channelType of Object.keys(CHANNEL_PATTERNS)) {
      const channelName = buildChannelName(channelType as ChannelType, params);
      availability[channelType as ChannelType] = {
        canBuild: channelName !== null,
        channelName,
      };
    }
    
    return availability;
  }, [params]);

  const handleParamChange = useCallback((key: keyof SubscriptionParams, value: string) => {
    setParams(prev => ({
      ...prev,
      [key]: value.trim() || undefined,
    }));
  }, []);

  const handleChannelToggle = useCallback((channelType: ChannelType, enabled: boolean) => {
    setEnabledChannels(prev => {
      const updated = new Set(prev);
      if (enabled) {
        updated.add(channelType);
      } else {
        updated.delete(channelType);
      }
      return updated;
    });
  }, []);

  // Subscribe/unsubscribe effect based on enabledChannels and params
  useEffect(() => {
    // Subscribe to enabled channels
    for (const channelType of enabledChannels) {
      const availability = channelAvailability[channelType];
      if (availability.canBuild && availability.channelName) {
        // Only subscribe if not already subscribed
        if (!activeSubscriptions.has(availability.channelName)) {
          onSubscribe(channelType, availability.channelName, params);
        }
      }
    }

    // Unsubscribe from channels that are no longer enabled or can't be built
    for (const activeChannel of activeSubscriptions) {
      // Find which channel type this belongs to
      let shouldUnsubscribe = true;
      
      for (const channelType of enabledChannels) {
        const availability = channelAvailability[channelType];
        if (availability.channelName === activeChannel) {
          shouldUnsubscribe = false;
          break;
        }
      }
      
      if (shouldUnsubscribe) {
        onUnsubscribe(activeChannel);
      }
    }
  }, [enabledChannels, channelAvailability, params, onSubscribe, onUnsubscribe, activeSubscriptions]);

  // All possible parameter fields
  const allParamFields: Array<keyof SubscriptionParams> = ['workspaceId', 'conversationId', 'threadId', 'participantId'];

  return (
    <div className="relative">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        title="Manage WebSocket subscriptions"
      >
        <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
        </svg>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Subscriptions {activeSubscriptions.size > 0 && `(${activeSubscriptions.size})`}
        </span>
        <svg className={`w-3 h-3 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full mt-2 right-0 w-[480px] max-h-[calc(100vh-120px)] overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50">
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Channel Subscriptions
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            {/* Parameter Inputs (single form for all channels) */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                Channel Parameters
              </div>
              {allParamFields.map((param) => (
                <div key={param}>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    {param}
                  </label>
                  <input
                    type="text"
                    value={params[param] || ''}
                    onChange={(e) => handleParamChange(param, e.target.value)}
                    placeholder={param === 'participantId' ? 'participant-id' : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'}
                    className="w-full px-2 py-1.5 text-xs font-mono border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              ))}
            </div>

            {/* Channel Type Checkboxes */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                Active Channels
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(Object.entries(CHANNEL_PATTERNS) as Array<[ChannelType, typeof CHANNEL_PATTERNS[ChannelType]]>).map(([channelType, pattern]) => {
                  const availability = channelAvailability[channelType];
                  const isEnabled = enabledChannels.has(channelType);
                  const isSubscribed = availability.channelName ? activeSubscriptions.has(availability.channelName) : false;
                  
                  return (
                    <div key={channelType} className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-900 rounded">
                      <input
                        type="checkbox"
                        id={`channel-${channelType}`}
                        checked={isEnabled}
                        disabled={!availability.canBuild}
                        onChange={(e) => handleChannelToggle(channelType, e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label
                        htmlFor={`channel-${channelType}`}
                        className={`flex-1 cursor-pointer ${!availability.canBuild ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                            {channelType}
                          </span>
                          {isSubscribed && (
                            <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                              ✓ Active
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                          {pattern.description}
                        </div>
                        {availability.canBuild && availability.channelName && (
                          <div className="text-xs font-mono text-gray-500 dark:text-gray-500 mt-1 break-all">
                            {availability.channelName}
                          </div>
                        )}
                        {!availability.canBuild && (
                          <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                            Missing: {pattern.requiredParams.filter(p => !params[p]).join(', ')}
                          </div>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Active Subscriptions Summary */}
            {activeSubscriptions.size > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Active Subscriptions ({activeSubscriptions.size})
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {Array.from(activeSubscriptions).map((channel) => (
                    <div
                      key={channel}
                      className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded text-xs"
                    >
                      <span className="font-mono text-gray-700 dark:text-gray-300 truncate flex-1">
                        {channel}
                      </span>
                      <button
                        onClick={() => onUnsubscribe(channel)}
                        className="ml-2 text-red-500 hover:text-red-600 text-xs"
                        title="Unsubscribe"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
